/**
 * MobileNavBar — bottom tab bar + "More" hamburger drawer for mobile.
 * - Shows 4 primary role-appropriate tabs
 * - A "More" button opens a full slide-up Sheet with ALL nav items,
 *   profile link, and admin settings (fixing issues 3, 4, 5)
 */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Sheet, SheetContent, SheetTitle } from '../ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  LayoutDashboard, Building2, Gavel, BarChart3, DollarSign,
  CalendarDays, Settings, Tag, Contact, Menu, User,
  FileText, ChevronRight, LogOut, X, Mail, Warehouse, CircleStar,
} from 'lucide-react';

const HorseIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 4 L6 10 C6 14.4 8.7 18 12 18 C15.3 18 18 14.4 18 10 L18 4" />
  </svg>
);

// Primary tabs (max 4) shown in the bar
const PRIMARY_TABS = {
  tenant_system_admin: [
    { label: 'Home',     icon: LayoutDashboard, href: '/dashboard' },
    { label: 'Horses',   icon: HorseIcon,       href: '/horses' },
    { label: 'FarmFolio',icon: Warehouse,       href: '/farmfolio' },
    { label: 'Accounts', icon: Building2,       href: '/crm/accounts' },
    { label: 'Sales',    icon: Gavel,           href: '/sales' },
  ],
  tenant_acct_admin: [
    { label: 'Home',     icon: LayoutDashboard, href: '/dashboard' },
    { label: 'Horses',   icon: HorseIcon,       href: '/horses' },
    { label: 'FarmFolio',icon: Warehouse,       href: '/farmfolio' },
    { label: 'Accounts', icon: Building2,       href: '/crm/accounts' },
    { label: 'Sales',    icon: Gavel,           href: '/sales' },
  ],
  tenant_acct_manager: [
    { label: 'Home',     icon: LayoutDashboard, href: '/dashboard' },
    { label: 'Horses',   icon: HorseIcon,       href: '/horses' },
    { label: 'FarmFolio',icon: Warehouse,       href: '/farmfolio' },
    { label: 'Accounts', icon: Building2,       href: '/crm/accounts' },
    { label: 'Sales',    icon: Gavel,           href: '/sales' },
  ],
  tenant_team_member: [
    { label: 'Horses',   icon: HorseIcon,       href: '/horses' },
    { label: 'FarmFolio',icon: Warehouse,       href: '/farmfolio' },
    { label: 'Accounts', icon: Building2,       href: '/crm/accounts' },
    { label: 'Contacts', icon: Contact,         href: '/crm/contacts' },
    { label: 'Calendar', icon: CalendarDays,    href: '/calendar' },
  ],
  tenant_team_leader: [
    { label: 'Horses',   icon: HorseIcon,       href: '/horses' },
    { label: 'FarmFolio',icon: Warehouse,       href: '/farmfolio' },
    { label: 'Accounts', icon: Building2,       href: '/crm/accounts' },
    { label: 'Sales',    icon: Gavel,           href: '/sales' },
    { label: 'Reports',  icon: BarChart3,       href: '/reports' },
  ],
  tenant_card_runner: [
    { label: 'Sales',    icon: Gavel,           href: '/sales' },
  ],
  tenant_sale_clerk: [
    { label: 'Sales',    icon: Gavel,           href: '/sales' },
  ],
  tenant_other: [
    { label: 'Horses',   icon: HorseIcon,       href: '/horses' },
    { label: 'FarmFolio',icon: Warehouse,       href: '/farmfolio' },
    { label: 'Accounts', icon: Building2,       href: '/crm/accounts' },
    { label: 'Reports',  icon: BarChart3,       href: '/reports' },
  ],
  owner: [
    { label: 'Portal',   icon: LayoutDashboard, href: '/owner-portal' },
    { label: 'Horses',   icon: HorseIcon,       href: '/owner-portal?tab=horses' },
    { label: 'Sales',    icon: Gavel,           href: '/owner-portal?tab=sales' },
    { label: 'Docs',     icon: FileText,        href: '/owner-portal?tab=documents' },
  ],
};

// All nav sections shown in the More drawer
const MORE_NAV = {
  tenant_system_admin: [
    { section: 'Virtual Rolodex' },
    { label: 'Accounts',           icon: Building2,    href: '/crm/accounts' },
    { label: 'Contacts',           icon: Contact,      href: '/crm/contacts' },
    { label: 'Horses',             icon: HorseIcon,    href: '/horses' },
    { label: 'FarmFolio',          icon: Warehouse,    href: '/farmfolio' },
    { section: 'Sales' },
    { label: 'Active Sales',       icon: Gavel,        href: '/sales' },
    { label: 'Sale Opportunities', icon: Tag,          href: '/sales/opportunities' },
    { section: 'Financials' },
    { label: 'Invoices',           icon: DollarSign,   href: '/invoices' },
    { section: 'Activity' },
    { label: 'Calendar',           icon: CalendarDays, href: '/calendar' },
    { label: 'Tasks',              icon: FileText,     href: '/tasks' },
    { label: 'Reports',            icon: BarChart3,    href: '/reports' },
  ],
  tenant_acct_admin: [
    { section: 'Virtual Rolodex' },
    { label: 'Accounts',           icon: Building2,    href: '/crm/accounts' },
    { label: 'Contacts',           icon: Contact,      href: '/crm/contacts' },
    { label: 'Horses',             icon: HorseIcon,    href: '/horses' },
    { label: 'FarmFolio',          icon: Warehouse,    href: '/farmfolio' },
    { section: 'Sales' },
    { label: 'Active Sales',       icon: Gavel,        href: '/sales' },
    { label: 'Sale Opportunities', icon: Tag,          href: '/sales/opportunities' },
    { section: 'Financials' },
    { label: 'Invoices',           icon: DollarSign,   href: '/invoices' },
    { section: 'Activity' },
    { label: 'Calendar',           icon: CalendarDays, href: '/calendar' },
    { label: 'Tasks',              icon: FileText,     href: '/tasks' },
    { label: 'Reports',            icon: BarChart3,    href: '/reports' },
  ],
  tenant_acct_manager: [
    { section: 'Virtual Rolodex' },
    { label: 'Accounts',           icon: Building2,    href: '/crm/accounts' },
    { label: 'Contacts',           icon: Contact,      href: '/crm/contacts' },
    { label: 'Horses',             icon: HorseIcon,    href: '/horses' },
    { label: 'FarmFolio',          icon: Warehouse,    href: '/farmfolio' },
    { section: 'Sales' },
    { label: 'Active Sales',       icon: Gavel,        href: '/sales' },
    { label: 'Sale Opportunities', icon: Tag,          href: '/sales/opportunities' },
    { section: 'Financials' },
    { label: 'Invoices',           icon: DollarSign,   href: '/invoices' },
    { section: 'Activity' },
    { label: 'Calendar',           icon: CalendarDays, href: '/calendar' },
    { label: 'Tasks',              icon: FileText,     href: '/tasks' },
    { label: 'Reports',            icon: BarChart3,    href: '/reports' },
  ],
  tenant_team_member: [
    { section: 'Virtual Rolodex' },
    { label: 'Accounts',           icon: Building2,    href: '/crm/accounts' },
    { label: 'Contacts',           icon: Contact,      href: '/crm/contacts' },
    { label: 'Horses',             icon: HorseIcon,    href: '/horses' },
    { label: 'FarmFolio',          icon: Warehouse,    href: '/farmfolio' },
    { section: 'Activity' },
    { label: 'Calendar',           icon: CalendarDays, href: '/calendar' },
    { label: 'Tasks',              icon: FileText,     href: '/tasks' },
    { label: 'Reports',            icon: BarChart3,    href: '/reports' },
  ],
  tenant_team_leader: [
    { section: 'Virtual Rolodex' },
    { label: 'Accounts',           icon: Building2,    href: '/crm/accounts' },
    { label: 'Contacts',           icon: Contact,      href: '/crm/contacts' },
    { label: 'Horses',             icon: HorseIcon,    href: '/horses' },
    { label: 'FarmFolio',          icon: Warehouse,    href: '/farmfolio' },
    { section: 'Sales' },
    { label: 'Active Sales',       icon: Gavel,        href: '/sales' },
    { label: 'Sale Opportunities', icon: Tag,          href: '/sales/opportunities' },
    { section: 'Activity' },
    { label: 'Calendar',           icon: CalendarDays, href: '/calendar' },
    { label: 'Tasks',              icon: FileText,     href: '/tasks' },
    { label: 'Reports',            icon: BarChart3,    href: '/reports' },
  ],
  tenant_card_runner: [
    { label: 'Active Sales',       icon: Gavel,        href: '/sales' },
  ],
  tenant_sale_clerk: [
    { label: 'Active Sales',       icon: Gavel,        href: '/sales' },
  ],
  tenant_other: [
    { section: 'Virtual Rolodex' },
    { label: 'Accounts',           icon: Building2,    href: '/crm/accounts' },
    { label: 'Contacts',           icon: Contact,      href: '/crm/contacts' },
    { label: 'Horses',             icon: HorseIcon,    href: '/horses' },
    { label: 'FarmFolio',          icon: Warehouse,    href: '/farmfolio' },
    { label: 'Reports',            icon: BarChart3,    href: '/reports' },
  ],
  owner: [
    { label: 'Owner Portal',       icon: LayoutDashboard, href: '/owner-portal' },
    { label: 'My Horses',          icon: HorseIcon,    href: '/owner-portal?tab=horses' },
    { label: 'Sale Activity',      icon: Gavel,        href: '/owner-portal?tab=sales' },
    { label: 'Documents',          icon: FileText,     href: '/owner-portal?tab=documents' },
  ],
};

function getPrimaryRole(user) {
  if (!user) return null;
  const roles = user.roles || [];
  if (roles.includes('system_owner'))        return 'system_owner';
  if (roles.includes('tenant_system_admin')) return 'tenant_system_admin';
  if (roles.includes('tenant_acct_admin'))   return 'tenant_acct_admin';
  if (roles.includes('tenant_acct_manager')) return 'tenant_acct_manager';
  if (roles.includes('tenant_team_member'))  return 'tenant_team_member';
  if (roles.includes('tenant_team_leader'))  return 'tenant_team_leader';
  if (roles.includes('tenant_card_runner'))  return 'tenant_card_runner';
  if (roles.includes('tenant_sale_clerk'))   return 'tenant_sale_clerk';
  if (roles.includes('tenant_other'))        return 'tenant_other';
  if (roles.includes('owner'))               return 'owner';
  return null;
}

const MobileNavBar = () => {
  const { user, logout, isTenantAdmin, isSyndicator, isFarm } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const role = getPrimaryRole(user);

  // Filter FarmFolio out when tenant is not a farm; inject My Syndicates when syndicator
  const tabs = ((role && PRIMARY_TABS[role]) || [])
    .filter(t => t.href !== '/farmfolio' || isFarm);

  const baseSections = (role && MORE_NAV[role]) || [];
  const moreSections = baseSections
    .filter(item => item.href !== '/farmfolio' || isFarm)
    .reduce((acc, item) => {
      acc.push(item);
      if (item.label === 'Accounts' && isSyndicator) {
        acc.push({ label: 'My Syndicates', icon: CircleStar, href: '/syndicates' });
      }
      return acc;
    }, []);

  if (tabs.length === 0) return null;

  const initials = user
    ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || '?'
    : '?';

  const handleMoreNav = (href) => {
    setMoreOpen(false);
    navigate(href);
  };

  return (
    <>
      <nav className="mobile-nav-bar" aria-label="Mobile navigation" data-testid="mobile-nav-bar">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = location.pathname === tab.href ||
            (tab.href !== '/' && location.pathname.startsWith(tab.href.split('?')[0]));
          return (
            <Link
              key={tab.href}
              to={tab.href}
              className={`mobile-nav-item ${isActive ? 'active' : ''}`}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon className="mobile-nav-icon" />
              <span className="mobile-nav-label">{tab.label}</span>
            </Link>
          );
        })}
        <button
          className={`mobile-nav-item ${moreOpen ? 'active' : ''}`}
          onClick={() => setMoreOpen(true)}
          aria-label="More navigation"
          data-testid="mobile-more-btn"
        >
          <Menu className="mobile-nav-icon" />
          <span className="mobile-nav-label">More</span>
        </button>
      </nav>

      {/* More drawer */}
      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent
          side="bottom"
          className="p-0 mobile-more-sheet rounded-t-2xl"
          style={{ height: '88vh', zIndex: 99999 }}
        >
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

          {/* Single scroll container — header is sticky so it never moves */}
          <div className="h-full overflow-y-auto" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0))' }}>

            {/* Sticky header — glued to top of scroll container */}
            <div
              className="flex items-center justify-between px-4 py-3 border-b"
              style={{ position: 'sticky', top: 0, zIndex: 10, background: 'hsl(var(--background))' }}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarImage src={user?.profile_photo_url} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">{initials}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="font-semibold text-sm leading-tight truncate">{user?.first_name} {user?.last_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                </div>
              </div>
              <button
                onClick={() => setMoreOpen(false)}
                className="p-2 rounded-full hover:bg-muted/60 transition-colors shrink-0 ml-3"
                aria-label="Close menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Nav sections */}
            <div className="px-2 pt-1">
              {moreSections.map((item, i) => {
                if (item.section) {
                  return (
                    <p key={i} className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 pt-4 pb-1">
                      {item.section}
                    </p>
                  );
                }
                const Icon = item.icon;
                // Action items (e.g. Messages panel) dispatch a custom event
                if (item.action) {
                  return (
                    <button
                      key={item.action}
                      onClick={() => { setMoreOpen(false); window.dispatchEvent(new CustomEvent(item.action)); }}
                      className="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-lg transition-colors text-sm hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </div>
                      <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    </button>
                  );
                }
                const isActive = location.pathname.startsWith(item.href.split('?')[0]);
                return (
                  <button
                    key={item.href}
                    onClick={() => handleMoreNav(item.href)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-3 rounded-lg transition-colors text-sm
                      ${isActive ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted/50'}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Icon className="w-4 h-4 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  </button>
                );
              })}
            </div>

            {/* Account links */}
            <div className="mx-2 mt-3 pt-3 border-t space-y-1">
              <button aria-label="Profile" onClick={() => handleMoreNav('/profile')}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm hover:bg-muted/50 transition-colors">
                <User className="w-4 h-4 text-primary" /><span>My Profile</span>
              </button>
              {role !== 'owner' && role !== null && (
                <button onClick={() => { setMoreOpen(false); window.dispatchEvent(new CustomEvent('open-messages')); }}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm hover:bg-muted/50 transition-colors">
                  <Mail className="w-4 h-4 text-primary" /><span>Messages</span>
                </button>
              )}
              {isTenantAdmin() && (
                <button onClick={() => handleMoreNav('/tenant-admin')}
                  className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm hover:bg-muted/50 transition-colors">
                  <Settings className="w-4 h-4 text-primary" /><span>Admin Portal</span>
                </button>
              )}
              <button onClick={() => { setMoreOpen(false); logout(); }}
                className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors">
                <LogOut className="w-4 h-4" /><span>Sign Out</span>
              </button>
            </div>

          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default MobileNavBar;
